// Environment configuration for API endpoints
import { debugLog } from "../utils/debug";

// Domain-based branding configuration
// Maps domains/subdomains to specific interclub branding
const DOMAIN_BRANDING_CONFIG = {
  // Main public domain - uses default branding
  'interclubseries.com': {
    isDefault: true,
    brandingTitle: 'Interclub Competition Manager',
    brandingDescription: 'Welcome to the Interclub Competition Manager! Discover upcoming competitions, check qualifiers, and track your progress.',
    logoUrl: null, // Uses default logo
    headerImageUrl: null, // Uses default header
  },
  'www.interclubseries.com': {
    isDefault: true,
    brandingTitle: 'Interclub Competition Manager',
    brandingDescription: 'Welcome to the Interclub Competition Manager! Discover upcoming competitions, check qualifiers, and track your progress.',
    logoUrl: null,
    headerImageUrl: null,
  },
  // PNIC subdomain
  'pnic.interclubseries.com': {
    interclubId: 'pnic', // Maps to interclub in database
    // Falls through to database branding
  },
  // Legacy Firebase hosting domain
  'pnic-scores.web.app': {
    interclubId: 'pnic',
  },
  'pnic-scores.firebaseapp.com': {
    interclubId: 'pnic',
  },
};

// Get branding configuration based on current domain
export const getDomainBranding = () => {
  const hostname = window.location.hostname;
  
  // Check for exact match first
  if (DOMAIN_BRANDING_CONFIG[hostname]) {
    return DOMAIN_BRANDING_CONFIG[hostname];
  }
  
  // Check for subdomain pattern (e.g., pnic.interclubseries.com)
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0];
    const baseDomain = parts.slice(1).join('.');
    
    // If it's a subdomain of interclubseries.com, use the subdomain as interclubId
    if (baseDomain === 'interclubseries.com' && subdomain !== 'www') {
      return {
        interclubId: subdomain,
        isSubdomain: true,
      };
    }
  }
  
  // Default for localhost/development or unknown domains
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return {
      isDevelopment: true,
      // In development, load from database based on user's interclub
    };
  }
  
  // Unknown domain - use default branding
  return {
    isDefault: true,
    brandingTitle: 'Interclub Competition Manager',
    brandingDescription: 'Welcome to the Interclub Competition Manager! Discover upcoming competitions, check qualifiers, and track your progress.',
    logoUrl: null,
    headerImageUrl: null,
  };
};

// Check if we should use default branding (no customization)
export const shouldUseDefaultBranding = () => {
  const domainConfig = getDomainBranding();
  return domainConfig.isDefault === true;
};

// Get the interclub ID to load from database (if applicable)
export const getDomainInterclubId = () => {
  const domainConfig = getDomainBranding();
  return domainConfig.interclubId || null;
};

const getApiBaseUrl = () => {
  // Check if we're running in development mode
  // In React, NODE_ENV might not be set in browser, so check hostname and port
  if (
    process.env.NODE_ENV === "development" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  ) {
    // For development, use local emulator
    return "http://localhost:3009/pnic-scores/us-central1/api";
  }

  // Default to production
  return "https://us-central1-pnic-scores.cloudfunctions.net/api";
};

// Function to test if local emulator is accessible and fallback to production if needed
export const getApiBaseUrlWithFallback = async () => {
  const baseUrl = getApiBaseUrl();

  // If we're in development and trying to use local emulator, test it first
  if (
    (process.env.NODE_ENV === "development" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1") &&
    baseUrl.includes("localhost")
  ) {
    try {
      const response = await fetch(`${baseUrl}/health`, {
        method: "GET",
        mode: "cors",
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        debugLog("✅ Local emulator is accessible");
        return baseUrl;
      }
    } catch (error) {
      debugLog("❌ Local emulator not accessible, falling back to production");
      return "https://us-central1-pnic-scores.cloudfunctions.net/api";
    }
  }

  return baseUrl;
};

export const API_BASE_URL = getApiBaseUrl();

// Registration API is a separate Cloud Function, not under /api
export const REGISTRATION_BASE_URL = API_BASE_URL.replace('/api', '') + '/registration';

// Log the current API URL for debugging (only in debug mode)
debugLog("🔧 Debug Mode Active - Environment Details:");
debugLog("Current API Base URL:", API_BASE_URL);
debugLog("Environment:", process.env.NODE_ENV);
debugLog("Hostname:", window.location.hostname);
debugLog("Port:", window.location.port);

// Also log to console for immediate visibility
console.log("🔧 Environment Config Debug:");
console.log("NODE_ENV:", process.env.NODE_ENV);
console.log("API_BASE_URL:", API_BASE_URL);
console.log("Hostname:", window.location.hostname);
console.log("Port:", window.location.port);
